import { useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  Grid,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import { brandColors } from '@config/brandTokens';
import { buildCheckEmailPath, buildSignInPath, resolvePostLoginDestination } from '@utils/authRouting';

// Validation schema
const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
  first_name: z.string().min(2, 'First name must be at least 2 characters'),
  last_name: z.string().min(2, 'Last name must be at least 2 characters'),
  phone: z.string().min(10, 'Phone number is required'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterCoachPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const incomingReturnUrl = searchParams.get('returnUrl');
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await authService.registerCoach({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
      });
      
      if (response?.emailVerificationRequired) {
        if (response.verificationLink) {
          sessionStorage.setItem('encycgames.verifyLink', response.verificationLink);
        }
        navigate(
          buildCheckEmailPath({
            email: data.email,
            role: 'coach',
            returnUrl: incomingReturnUrl,
          }),
        );
        return;
      }

      if (response?.accessToken && response.user) {
        const userWithCoachData = {
          ...response.user,
          coachId: (response.user as any)?.coachId,
          coachIdCode: (response.user as any)?.coachIdCode,
          firstName: (response.user as any)?.firstName,
          lastName: (response.user as any)?.lastName,
        };

        login(userWithCoachData);
        const coachId = userWithCoachData.coachIdCode || userWithCoachData.coachId || 'N/A';
        toast.success(`You are registered as a coach. Your Coach ID is ${coachId}.`, {
          autoClose: 5000,
        });
        navigate(resolvePostLoginDestination(userWithCoachData, incomingReturnUrl));
        return;
      }

      if (response?.user) {
        navigate(
          buildSignInPath({
            returnUrl: incomingReturnUrl,
            registered: 'coach',
            email: data.email,
          }),
        );
        return;
      }

      throw new Error('Registration response is invalid');
    } catch (err: any) {
      const data = err.response?.data || {};
      const errorMessage = data.message || err.message || 'Registration failed. Please try again.';
      if (
        data.code === 'ACCOUNT_EXISTS_UNVERIFIED'
        || data.code === 'EMAIL_NOT_VERIFIED'
        || data.data?.emailVerificationRequired
      ) {
        if (data.data?.verificationLink) {
          sessionStorage.setItem('encycgames.verifyLink', data.data.verificationLink);
        }
        navigate(
          buildCheckEmailPath({
            email: data.email || getValues('email'),
            role: 'coach',
            returnUrl: incomingReturnUrl,
          }),
        );
        return;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell maxWidth="md">
      <AuthFormCard
        roleLabel="Coach"
        title="Coach registration"
        subtitle="You also get a player profile on this account for contests and self-eval. More coach tools come later."
        chrome
        backTo={buildSignInPath({ returnUrl: incomingReturnUrl })}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('first_name')}
                label="First Name"
                fullWidth
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                {...register('last_name')}
                label="Last Name"
                fullWidth
                error={!!errors.last_name}
                helperText={errors.last_name?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('email')}
                label="Email Address"
                type="email"
                fullWidth
                autoComplete="email"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('phone')}
                label="Phone Number"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('password')}
                label="Password"
                type="password"
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message || 'Must be at least 8 characters with uppercase, lowercase, and number'}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2, ...authPrimaryCtaSx }}
            disabled={isLoading}
          >
            {isLoading ? 'Registering...' : 'Register as coach'}
          </Button>

          <Box sx={{ mt: 1, textAlign: 'center', ...authLinkSx }}>
            <Typography variant="body2" sx={{ color: brandColors.slate }}>
              Already have an account?{' '}
              <Link component={RouterLink} to={buildSignInPath({ returnUrl: incomingReturnUrl })}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </AuthFormCard>
    </AuthPageShell>
  );
};

export default RegisterCoachPage;
